import React, { useEffect, useState, useCallback } from 'react';
import { Box, Paper, Typography, Button, IconButton } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';

const CLE_REPORT = 'pwa-install-reporte-le';
const DUREE_REPORT_MS = 7 * 24 * 60 * 60 * 1000;

// L'événement peut survenir avant le montage du composant : on le capte dès le
// chargement du module, puis on prévient les composants abonnés.
let invitationInstallation = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    invitationInstallation = e;
    window.dispatchEvent(new Event('pwa-installable'));
  });
  window.addEventListener('appinstalled', () => {
    invitationInstallation = null;
    window.dispatchEvent(new Event('pwa-installable'));
  });
}

const estInstallee = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);

const estIOS = () =>
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios|edgios/i.test(navigator.userAgent); // Safari uniquement

const recemmentReporte = () => {
  try {
    const t = parseInt(localStorage.getItem(CLE_REPORT) || '0', 10);
    return t && Date.now() - t < DUREE_REPORT_MS;
  } catch (_) {
    return false;
  }
};

/**
 * Bouton "Installer l'application" (PWA).
 * - Android / Chrome / Edge : utilise l'invitation d'installation du navigateur.
 * - iPhone / iPad (Safari) : pas d'invitation possible, on affiche la marche à suivre.
 * - Rien n'est affiché si l'application est déjà installée ou si l'utilisateur a
 *   répondu "Plus tard" depuis moins de 7 jours.
 */
const InstallPWA = () => {
  const [installable, setInstallable] = useState(!!invitationInstallation);
  const [masque, setMasque] = useState(recemmentReporte());
  const [installee, setInstallee] = useState(estInstallee());

  useEffect(() => {
    const maj = () => {
      setInstallable(!!invitationInstallation);
      setInstallee(estInstallee());
    };
    window.addEventListener('pwa-installable', maj);
    return () => window.removeEventListener('pwa-installable', maj);
  }, []);

  const reporter = useCallback(() => {
    try { localStorage.setItem(CLE_REPORT, String(Date.now())); } catch (_) { /* ignoré */ }
    setMasque(true);
  }, []);

  const installer = async () => {
    if (!invitationInstallation) return;
    invitationInstallation.prompt();
    const choix = await invitationInstallation.userChoice;
    invitationInstallation = null;
    setInstallable(false);
    if (choix.outcome !== 'accepted') reporter();
  };

  const ios = estIOS();
  if (installee || masque || (!installable && !ios)) return null;

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label="Installer l'application"
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 'auto' },
        right: { xs: 12, sm: 24 },
        bottom: { xs: 12, sm: 24 },
        maxWidth: { sm: 380 },
        p: 2,
        zIndex: (theme) => theme.zIndex.snackbar,
        borderLeft: '6px solid #2E7D32',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box component="img" src="/logo192.png" alt="" sx={{ width: 48, height: 48, borderRadius: 1, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            Installer l'application
          </Typography>
          {ios && !installable ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Appuyez sur <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} /> <b>Partager</b>, puis
              sur <b>« Sur l'écran d'accueil »</b>.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Accédez à BON'EPEY'A NTOCKO en un clic depuis votre écran d'accueil, comme une application.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            {installable && (
              <Button variant="contained" size="small" startIcon={<GetAppIcon />} onClick={installer}>
                Installer
              </Button>
            )}
            <Button size="small" onClick={reporter}>
              {installable ? 'Plus tard' : 'Compris'}
            </Button>
          </Box>
        </Box>
        <IconButton size="small" aria-label="Fermer" onClick={reporter} sx={{ mt: -0.5, mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default InstallPWA;
